import React, {FC, ReactElement, useCallback, useState} from "react";
import {FieldArrayRenderProps} from "formik";
import {Button, Card, CardActions, CardContent, Grid} from "@mui/material";
import TextFieldWithValidation from "../../../components/input/TextFieldWithValidation";
import AddCircleOutlinedIcon from "@mui/icons-material/AddCircleOutlined";
import {BankDetailsOnInvoiceDto} from "../../../model/company/InvoiceDataDto";
import CancelIcon from "@mui/icons-material/Cancel";
import {observer} from "mobx-react";
import {AlertColor} from "@mui/material/Alert/Alert";
import {Trans, useTranslation} from "react-i18next";
import {logEvent} from "../../../utils/SentryUtils";

const EST_BANKS = [
    ["Coop Pank aktsiaselts", "EKRDEE22", "42"],
    ["Eesti Pank", "EPBEEE2X", "16"],
    ["AS SEB Pank", "EEUHEE2X", "10"],
    ["Swedbank AS", "HABAEE2X", "22"],
    ["Luminor Bank AS", "RIKOEE22", "17"],
    ["Luminor Bank AS", "RIKOEE22", "96"],
    ["AS Citadele banka Eesti filiaal", "PARXEE22", "12"],
    ["Svenska Handelsbanken AB Eesti filiaal", "HANDEE22", "83"],
    ["AS TBB pank", "TABUEE22", "00"],
    ["OP Corporate Bank plc Eesti filiaal", "OKOYEE2X", "51"],
    ["AS LHV Pank", "LHVBEE22", "77"],
    ["Bigbank AS", "BIGKEE2B", "75"],
]

export const BankDetailsComponent: FC<{
    arrHelper: FieldArrayRenderProps,
    bankDetails: BankDetailsOnInvoiceDto[],
    existingItem: BankDetailsOnInvoiceDto | undefined,
    showSnackBar: (messageType: AlertColor, messageText: string) => void
}> = observer((props): ReactElement => {
    const {arrHelper, showSnackBar, existingItem, bankDetails} = props;
    const [iban, setIban] = useState<string>(existingItem ? existingItem.iban : '');
    const [bankName, setBankName] = useState<string | undefined>(existingItem?.bank);
    const [footer, setFooter] = useState<string | undefined>(existingItem?.footer);
    const disabled = existingItem !== undefined;

    const {t} = useTranslation();

    const onAddIban = useCallback(() => {
        if (!iban) {
            showSnackBar("error", t(`company_settings.invoice.account_error`, "Please select IBAN"))
            return
        }
        if (!bankName) {
            showSnackBar("error", t(`company_settings.invoice.bank_detail_error`, "Please specify bank details"))
            return
        }

        const newItem: BankDetailsOnInvoiceDto = {
            iban: iban.toString(),
            bank: bankName,
            footer: footer
        }
        arrHelper.push(newItem)
        setIban('');
        setBankName("");
        setFooter("");
        logEvent("company_settings.invoice_details", "Added new bank");
    }, [iban, bankName, footer, t, showSnackBar, arrHelper]);

    const onDeleteIban = useCallback(() => {
        if (existingItem) {
            arrHelper.remove(bankDetails.indexOf(existingItem))
            logEvent("company_settings.invoice_details", "Bank removed");
        }
    }, [bankDetails, existingItem, arrHelper]);

    const onIbanChanged = (e: any) => {
        const newIban = e.target.value;
        setIban(newIban);
        setBankName("");
        setFooter("");
        if (newIban) {
            const knownDetails = EST_BANKS.find(it => newIban.startsWith('EE') && newIban.startsWith(it[2], 4));
            if (knownDetails) {
                setBankName(knownDetails[0])
                setFooter(knownDetails[1])
            }
        }
    };
    return <>
        <Card variant={"outlined"} style={{width: "22rem"}}>
            <CardContent>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextFieldWithValidation
                            value={iban}
                            label="IBAN"
                            placeholder="EE497700771001487456"
                            disabled={disabled}
                            style={{minWidth: "20rem"}}
                            onChange={onIbanChanged}/>
                    </Grid>
                    <Grid item xs={12}>
                        <TextFieldWithValidation value={bankName}
                                                 label={<Trans i18nKey={`company_settings.invoice.bank_detail`}>Bank details</Trans>}
                                                 disabled={disabled}
                                                 placeholder="AS LHV Pank"
                                                 style={{minWidth: "20rem"}}
                                                 onChange={e => setBankName(e.target.value)}/>
                    </Grid>
                    <Grid item xs={12}>
                        <TextFieldWithValidation value={footer}
                                                 label={<Trans i18nKey={`company_settings.invoice.additional_bank_detail`}>Additional bank details</Trans>}
                                                 placeholder="SWIFT, BIC..."
                                                 disabled={disabled}
                                                 style={{minWidth: "20rem"}}
                                                 onChange={e => setFooter(e.target.value)}/>
                    </Grid>
                </Grid>
            </CardContent>
            <CardActions>
                {existingItem &&
                    <Button startIcon={<CancelIcon/>} variant="contained" color="secondary" fullWidth onClick={onDeleteIban}>
                        <Trans i18nKey="common.delete">Delete</Trans>
                    </Button>
                }
                {!existingItem &&
                    <Button startIcon={<AddCircleOutlinedIcon/>} variant={"contained"} color="primary" fullWidth onClick={onAddIban}>
                        <Trans i18nKey="common.add">Add</Trans>
                    </Button>
                }
            </CardActions>
        </Card>
    </>;
});